import { useEffect, useState } from 'react';
import type { SharingLegLike } from '@snowball/meprfo';
import { MePrFoService } from '@/services/meprfo';
import {
  RECIPIENT_CONFIRM_POLL_INITIAL_INTERVAL_MS,
  RECIPIENT_CONFIRM_POLL_BACKOFF_FACTOR,
  RECIPIENT_CONFIRM_POLL_MAX_INTERVAL_MS,
  RECIPIENT_CONFIRM_POLL_GIVE_UP_MS,
} from '@/modules/config';

/**
 * Whether a leg shows evidence that a recipient has confirmed receipt by
 * tapping its share link. The sender's own return is an *implied confirm* and
 * does NOT record a click-through (see LegRedirect), so a non-zero
 * click_thru_count means a recipient confirmed — never the sender.
 */
export function legHasRecipientConfirm(leg: SharingLegLike | null): boolean {
  return !!leg && leg.click_thru_count > 0;
}

/**
 * Live "recipient-confirmed" detection for the sender's confirm pages.
 *
 * While the sender is still sending, a recipient can tap the leg link to
 * confirm receipt before the sender returns to Snowball. That click bumps the
 * leg's click_thru_count on the backend, but nothing on the sender's page
 * re-reads the leg — so the status would otherwise stay stale at
 * "unconfirmed" (issue #341).
 *
 * This hook polls the leg while the page is visible and returns true once a
 * recipient has confirmed. It fetches immediately on mount/when args change and
 * again whenever the tab becomes visible (covering the sender returning from the
 * SMS app). Between polls it backs off exponentially — starting at
 * RECIPIENT_CONFIRM_POLL_INITIAL_INTERVAL_MS and growing by
 * RECIPIENT_CONFIRM_POLL_BACKOFF_FACTOR up to
 * RECIPIENT_CONFIRM_POLL_MAX_INTERVAL_MS — and gives up altogether after
 * RECIPIENT_CONFIRM_POLL_GIVE_UP_MS so a tab left open never polls forever
 * (issue #388). Polling pauses while the tab is
 * hidden and stops on unmount. The result latches true and resets when legId
 * changes.
 *
 * Pass enabled=false to skip polling entirely (e.g. when already confirmed via
 * the implied-confirm path, or on a variant where the status isn't shown).
 */
export function useRecipientConfirm(
  legId: string | null,
  enabled: boolean
): boolean {
  const [recipientConfirmed, setRecipientConfirmed] = useState(false);

  useEffect(() => {
    // Reset when the leg changes so a new leg starts unconfirmed.
    setRecipientConfirmed(false);

    if (!enabled || !legId) {
      return;
    }

    // `stopped` covers unmount, a confirmed recipient, and giving up after the
    // deadline — any of which means no further polling should happen.
    let stopped = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let nextInterval = RECIPIENT_CONFIRM_POLL_INITIAL_INTERVAL_MS;
    const deadline = Date.now() + RECIPIENT_CONFIRM_POLL_GIVE_UP_MS;

    const clearTimer = () => {
      if (timer !== null) {
        clearTimeout(timer);
        timer = null;
      }
    };

    const poll = () => {
      MePrFoService.getSharingLeg(legId)
        .then(leg => {
          if (stopped) return;
          if (legHasRecipientConfirm(leg)) {
            setRecipientConfirmed(true);
            stopped = true;
            clearTimer();
          }
        })
        .catch(() => {
          // Best-effort: a failed poll just leaves the status unchanged.
        });
    };

    // Schedule the next backed-off poll, unless we're stopped, the tab is
    // hidden, or the give-up deadline has passed.
    const scheduleNext = () => {
      clearTimer();
      if (stopped || document.visibilityState !== 'visible') return;
      const remaining = deadline - Date.now();
      if (remaining <= 0) {
        stopped = true;
        return;
      }
      timer = setTimeout(
        () => {
          poll();
          scheduleNext();
        },
        Math.min(nextInterval, remaining)
      );
      nextInterval = Math.min(
        nextInterval * RECIPIENT_CONFIRM_POLL_BACKOFF_FACTOR,
        RECIPIENT_CONFIRM_POLL_MAX_INTERVAL_MS
      );
    };

    const handleVisibility = () => {
      if (stopped) return;
      if (document.visibilityState === 'visible') {
        // The sender may have just returned from the SMS app — poll at once,
        // then resume the backed-off schedule.
        if (Date.now() < deadline) poll();
        scheduleNext();
      } else {
        clearTimer();
      }
    };

    // Fetch immediately, then poll while visible.
    poll();
    scheduleNext();
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      stopped = true;
      clearTimer();
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [legId, enabled]);

  return recipientConfirmed;
}
